import { SUPPORTED_MODELS, type ModelDefinition } from "@/shared/types";
// Provider display name + title-casing live in the centralized provider
// registry; imported for local use and re-exported so existing importers work.
import {
  getProviderDisplayName,
  titleCaseProviderKey,
} from "@/lib/provider-registry";

/**
 * The selectable catalog is the whole of SUPPORTED_MODELS. The sphinx server
 * holds the Anthropic and OpenAI keys and falls back to them when a request
 * carries none, so a browser-side key is never required — the Settings
 * "API key overrides" are optional. (The fork used to filter by configured
 * key and list the rest disabled; no surface needs that gate any more.)
 */
export function buildAvailableModels(): ModelDefinition[] {
  return SUPPORTED_MODELS;
}

/** Strip the redundant "(Free)" tier suffix for denser labels. */
export function compactModelLabel(name: string | undefined | null): string {
  if (!name) return "";
  return name.replace(/\s*\(Free\)\s*$/i, "").trim() || name;
}

// Re-exported from the centralized provider registry (imported at top) so
// existing importers of these names keep working.
export { getProviderDisplayName, titleCaseProviderKey };

export interface ModelMenuItem {
  id: string;
  name: string;
  provider: string;
}

export interface ModelMenuGroup<T extends ModelMenuItem> {
  /** Group key — the provider name. */
  provider: string;
  title: string;
  models: T[];
}

/** Group models by provider, providers sorted alphabetically. */
export function buildModelMenuGroups<T extends ModelMenuItem>(
  models: T[]
): ModelMenuGroup<T>[] {
  const byProvider = new Map<string, T[]>();
  for (const model of models) {
    const existing = byProvider.get(model.provider);
    if (existing) {
      existing.push(model);
    } else {
      byProvider.set(model.provider, [model]);
    }
  }

  return Array.from(byProvider.keys())
    .sort()
    .map((provider) => ({
      provider,
      title: getProviderDisplayName(provider),
      models: byProvider.get(provider) ?? [],
    }));
}

/**
 * The default pick is the first selectable model in catalog order
 * (SUPPORTED_MODELS leads with Claude Opus 5); falls back to the first row
 * even when every row is disabled so callers always get a definition.
 */
export const getDefaultModel = (
  availableModels: ModelDefinition[]
): ModelDefinition => {
  for (const catalogModel of SUPPORTED_MODELS) {
    const found = availableModels.find(
      (m) => m.id === catalogModel.id && !m.disabled
    );
    if (found) return found;
  }
  return availableModels[0];
};
